import { useCallback, useMemo, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { Grid, GridColumn } from "simplydo/react/ui/grid";
import { H3, Subheader } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";

import actions from "actions";
import api from "api";
import { useAppSelector } from "store";

import SettingActions from "components/lib/SettingActions";

const OrgTagChooser = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useAppSelector((state) => state.user);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const tagList = user?.ownerOrganisation?.tagList;
  const availableUserTags = useMemo(
    () =>
      tagList?.filter((tag) => !tag?.disabledEntities?.includes("users")) ?? [],
    [tagList],
  );

  const toggleTag = useCallback((tag: OpenAPI.Schemas["Tag"]) => {
    setTagIds((prevTags) => {
      const newTags = [...prevTags];
      const tagIndex = newTags.indexOf(tag._id);
      if (tagIndex > -1) {
        newTags.splice(tagIndex, 1);
      } else {
        newTags.push(tag._id);
      }
      return newTags;
    });
  }, []);

  const saveTags = useCallback(() => {
    if (!user) {
      return;
    }
    setSaving(true);
    api.tags.tagDocuments(
      "users",
      tagIds.filter((tagId) => !(user.tags || []).includes(tagId)), // Only send NEW tags
      [user._id],
      (ownerTags) => {
        setSaving(false);
        dispatch(actions.user.updateTags(ownerTags));
      },
      () => {
        setSaving(false);
        toast.error("Failed to select tags");
      },
    );
  }, [tagIds, user, dispatch]);

  return (
    <>
      <Helmet title={t("tags.title")} />
      <div
        style={{
          height: "100%",
          maxHeight: "69vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <H3>
          {t("tags.selectTags")}
          <Subheader>
            {t("tags.info", { orgName: user?.ownerOrganisation?.name })}
          </Subheader>
        </H3>
        <Message color="blue">{t("tags.chooseMany")}</Message>

        <div
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            flex: 1,
            marginTop: 10,
          }}
        >
          <Grid mobile={1} columns={2}>
            {availableUserTags.map((c) => (
              <GridColumn key={c._id}>
                <Checkbox
                  toggle
                  checked={tagIds.indexOf(c._id) > -1}
                  onChange={() => toggleTag(c)}
                >
                  {c.value}
                </Checkbox>
              </GridColumn>
            ))}
          </Grid>
        </div>
        <SettingActions>
          <Checkbox
            toggle
            style={{ marginRight: 30, marginBottom: 10 }}
            checked={tagIds.length > 0}
            onChange={() => {
              if (tagIds.length > 0) {
                setTagIds([]);
              } else {
                setTagIds(availableUserTags.map((t) => t._id));
              }
            }}
          >
            {tagIds.length > 0 ? "Deselect all" : "Select All"}
          </Checkbox>
          <Button
            onClick={() => {
              saveTags();
            }}
            loading={saving}
            disabled={!tagIds?.length}
            color="primary"
          >
            {t("generic.continue")}
          </Button>
        </SettingActions>
      </div>
    </>
  );
};

export default OrgTagChooser;
